/**
 * The application frame: header, navigation, announcement banner, footer.
 *
 * Responsive strategy: a single column with a slide-down menu below `md`, and a
 * horizontal navigation bar above it. Navigation order follows the document
 * direction automatically because the row is a flex container using logical
 * spacing -- in Arabic the whole bar mirrors, including the menu button.
 *
 * Branding comes from the library record: the church logo replaces the
 * open-book mark once staff upload one, and the browser tab shows the library
 * name. Until then both look exactly as they always have.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'

import { Badge, Button, cx } from '@/components/ui'
import { useDirection } from '@/hooks/useDirection'
import { useLibraryInfo } from '@/hooks/useLibraryInfo'
import { useLogout, useSession } from '@/hooks/useAuth'

import { LanguageToggle } from './LanguageToggle'

interface NavItem {
  to: string
  label: string
  staffOnly?: boolean
  authOnly?: boolean
}

/** The tab title before the library record has loaded (same as index.html). */
const DEFAULT_DOCUMENT_TITLE = 'Church Library · مكتبة الكنيسة'

/**
 * The church logo, or the built-in open-book mark when there is none. A logo
 * that fails to load (replaced since this page fetched the record) falls back
 * to the mark rather than showing a broken image.
 */
export const LibraryMark = ({ logoUrl }: { logoUrl?: string | null }) => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)

  if (logoUrl && logoUrl !== failedUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        aria-hidden="true"
        className="size-9 shrink-0 rounded-lg object-contain"
        onError={() => setFailedUrl(logoUrl)}
      />
    )
  }

  return (
    <span
      aria-hidden="true"
      className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand text-brand-contrast"
    >
      {/* Open-book mark */}
      <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 6.5C10.5 5.2 8.4 4.5 6 4.5H3.5v13H6c2.4 0 4.5.7 6 2 1.5-1.3 3.6-2 6-2h2.5v-13H18c-2.4 0-4.5.7-6 2Z" />
        <path d="M12 6.5v14" />
      </svg>
    </span>
  )
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'nav.home' },
  { to: '/browse', label: 'nav.browse' },
  { to: '/visit', label: 'nav.libraryInfo' },
  { to: '/reservations', label: 'nav.myReservations', authOnly: true },
  { to: '/admin', label: 'nav.admin', staffOnly: true },
]

export const AppShell = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation()
  const { language } = useDirection()
  const { user, isAuthenticated, isStaff } = useSession()
  const { data: info } = useLibraryInfo()
  const logout = useLogout()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  // A route change should always dismiss the mobile menu.
  useEffect(() => setMenuOpen(false), [location.pathname])

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.staffOnly) return isStaff
    if (item.authOnly) return isAuthenticated
    return true
  })

  const handleSignOut = () => {
    logout.mutate(undefined, { onSettled: () => navigate('/') })
  }

  const libraryName = info?.library_name || t('app.name')
  const documentTitle = info?.library_name || DEFAULT_DOCUMENT_TITLE

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])
  // Hidden until the library info says sign-up is open, so it never flashes.
  const signUpOpen = Boolean(info?.self_signup_enabled)

  return (
    <div className="flex min-h-screen flex-col bg-surface-sunken">
      <a
        href="#main-content"
        className="sr-only-focusable absolute z-50 m-2 rounded-lg bg-brand px-4 py-2 text-brand-contrast"
      >
        {t('app.skipToContent')}
      </a>

      <header className="sticky top-0 z-30 border-b border-border-subtle bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6">
          <Link to="/" className="flex min-w-0 items-center gap-2.5">
            <LibraryMark logoUrl={info?.logo_url} />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-ink sm:text-base">
                {libraryName}
              </span>
              {info?.church_name && (
                <span className="hidden truncate text-xs text-ink-subtle sm:block">
                  {info.church_name}
                </span>
              )}
            </span>
          </Link>

          <nav
            className="ms-auto hidden items-center gap-1 md:flex"
            aria-label={t('a11y.mainNavigation')}
          >
            {visibleItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  cx(
                    'rounded-lg px-3 py-2 text-sm font-medium transition',
                    isActive
                      ? 'bg-brand-soft text-brand-strong'
                      : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
                  )
                }
              >
                {t(item.label)}
              </NavLink>
            ))}
          </nav>

          <div className="ms-auto flex items-center gap-2 md:ms-0">
            <LanguageToggle />

            {isAuthenticated ? (
              <div className="hidden items-center gap-2 md:flex">
                <Link
                  to="/profile"
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
                >
                  <span className="max-w-[9rem] truncate">{user?.full_name}</span>
                  {isStaff && <Badge tone="brand">{t('auth.staffBadge')}</Badge>}
                </Link>
                <Button variant="ghost" size="sm" onClick={handleSignOut} loading={logout.isPending}>
                  {t('nav.signOut')}
                </Button>
              </div>
            ) : (
              <div className="hidden items-center gap-2 md:flex">
                <Button variant="ghost" size="sm" onClick={() => navigate('/sign-in')}>
                  {t('nav.signIn')}
                </Button>
                {signUpOpen && (
                  <Button size="sm" onClick={() => navigate('/sign-up')}>
                    {t('nav.signUp')}
                  </Button>
                )}
              </div>
            )}

            <button
              type="button"
              className="rounded-lg p-2 text-ink-muted transition hover:bg-surface-sunken md:hidden"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label={menuOpen ? t('nav.closeMenu') : t('nav.menu')}
            >
              <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2">
                {menuOpen ? (
                  <path d="M6 6l12 12M18 6L6 18" />
                ) : (
                  <path d="M4 7h16M4 12h16M4 17h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav
            id="mobile-nav"
            className="border-t border-border-subtle bg-surface px-4 py-3 md:hidden"
            aria-label={t('a11y.mainNavigation')}
          >
            <ul className="flex flex-col gap-1">
              {visibleItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      cx(
                        'block rounded-lg px-3 py-2.5 text-sm font-medium',
                        isActive
                          ? 'bg-brand-soft text-brand-strong'
                          : 'text-ink-muted hover:bg-surface-sunken',
                      )
                    }
                  >
                    {t(item.label)}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="mt-3 border-t border-border-subtle pt-3">
              {isAuthenticated ? (
                <div className="flex flex-col gap-2">
                  <Link
                    to="/profile"
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-ink-muted hover:bg-surface-sunken"
                  >
                    {t('nav.profile')} · {user?.full_name}
                  </Link>
                  <Button variant="secondary" size="sm" block onClick={handleSignOut}>
                    {t('nav.signOut')}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" block onClick={() => navigate('/sign-in')}>
                    {t('nav.signIn')}
                  </Button>
                  {signUpOpen && (
                    <Button size="sm" block onClick={() => navigate('/sign-up')}>
                      {t('nav.signUp')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </nav>
        )}
      </header>

      {info?.announcement_active && info.announcement && (
        <div className="border-b border-accent/30 bg-accent-soft">
          <p className="mx-auto max-w-6xl px-4 py-2.5 text-sm text-ink sm:px-6">
            <span aria-hidden="true" className="me-2">
              📣
            </span>
            {info.announcement}
          </p>
        </div>
      )}

      <main id="main-content" className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
        {children}
      </main>

      <footer className="border-t border-border-subtle bg-surface">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-6 text-sm text-ink-muted sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <p className="font-medium text-ink">{libraryName}</p>
            {info?.church_name && <p className="text-xs text-ink-subtle">{info.church_name}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <Link to="/visit" className="hover:text-ink">
              {t('nav.libraryInfo')}
            </Link>
            {info?.phone && (
              <a href={`tel:${info.phone}`} className="force-ltr hover:text-ink">
                {info.phone}
              </a>
            )}
            {info?.email && (
              <a href={`mailto:${info.email}`} className="force-ltr hover:text-ink">
                {info.email}
              </a>
            )}
            <span className="text-ink-subtle">{language.toUpperCase()}</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
